import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

interface ApiResource {
  '@id'?: string;
  id?: any;
  name?: string;
  label?: string;
  title?: string;
  reference?: string;
  picture?: string;
  color?: string;
}

interface PublicIssueForm {
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerAddress: string;
  title: string;
  description: string;
  item: string | null;
  type: string | null;
  priority: string | null;
}

@Component({
  selector: 'app-public-sav-issue',
  templateUrl: './public-sav-issue.component.html',
  styleUrls: ['./public-sav-issue.component.scss']
})
export class PublicSavIssueComponent implements OnInit {
  readonly lightLogo = 'assets/images/logo/logo-black-DDQLLIDb.png';
  readonly darkLogo = 'assets/images/logo/logo-white-Dz6Ska7-.png';

  items: ApiResource[] = [];
  issueTypes: ApiResource[] = [];
  priorities: ApiResource[] = [];
  selectedItem: ApiResource | null = null;
  requestedItemName = '';
  itemUnavailable = false;

  loading = true;
  submitting = false;
  cachedLoaderColor: string | null = null;
  errorMessage = '';
  successTicketNumber = '';
  selectedFiles: Array<{ file: File; preview: string | ArrayBuffer | null }> = [];
  mode: 'light' | 'dark' = 'light';
  currentLanguage: 'fr' | 'en' = 'fr';

  form: PublicIssueForm = {
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    title: '',
    description: '',
    item: null,
    type: null,
    priority: null
  };

  private readonly baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeLanguage();
    this.initializeMode();
    this.requestedItemName = (this.route.snapshot.queryParamMap.get('item') || '').trim();
    this.cachedLoaderColor = sessionStorage.getItem(`sav-color:${this.normalizeText(this.requestedItemName)}`);

    forkJoin({
      items: this.http.get<any>(`${this.baseUrl}/public/sav/items`),
      issueTypes: this.http.get<any>(`${this.baseUrl}/public/sav/issue-types`),
      priorities: this.http.get<any>(`${this.baseUrl}/public/sav/issue-priorities`)
    }).subscribe({
      next: ({items, issueTypes, priorities}) => {
        this.items = this.prepareOptions(this.extractCollection(items));
        this.issueTypes = this.prepareOptions(this.extractCollection(issueTypes));
        this.priorities = this.prepareOptions(this.extractCollection(priorities));
        this.applyQueryItem();
        this.loading = false;
      },
      error: () => {
        this.errorMessage = this.translate.instant('publicSavIssue.loadError');
        this.loading = false;
      }
    });
  }

  submit(): void {
    this.errorMessage = '';
    this.successTicketNumber = '';

    if (!this.isFormValid()) {
      this.errorMessage = this.translate.instant('publicSavIssue.requiredFields');
      return;
    }

    this.submitting = true;
    this.http.post<any>(`${this.baseUrl}/create/public/sav/issue`, this.buildPayload()).subscribe({
      next: response => {
        this.successTicketNumber = response?.['hydra:member']?.ticketNumber || response?.ticketNumber || response?.data?.ticketNumber || 'created';
        this.submitting = false;
        this.resetFormAfterSuccess();
      },
      error: error => {
        this.errorMessage = error?.['hydra:title'] || error?.detail || error?.message || this.translate.instant('publicSavIssue.submitError');
        this.submitting = false;
      }
    });
  }

  toggleMode(): void {
    this.mode = this.mode === 'light' ? 'dark' : 'light';
  }

  setLanguage(language: 'fr' | 'en'): void {
    this.currentLanguage = language;
    this.translate.use(language);
  }

  clearErrorMessage(): void {
    this.errorMessage = '';
  }

  clearSuccessMessage(): void {
    this.successTicketNumber = '';
  }

  getResourceLabel(resource: ApiResource): string {
    const reference = resource.reference ? `${resource.reference} - ` : '';
    return `${reference}${resource.name || resource.label || resource.title || resource.id || ''}`;
  }

  get mainStyle(): { [key: string]: string } {
    if (!this.loading) {
      return this.themeVars;
    }
    if (this.cachedLoaderColor) {
      return { '--sav-primary': this.cachedLoaderColor };
    }
    return {};
  }

  get logoPath(): string {
    return this.selectedItem?.picture || (this.mode === 'dark' ? this.darkLogo : this.lightLogo);
  }

  get themeVars(): { [key: string]: string } {
    const color = this.selectedItem?.color;
    if (!color) {
      return {};
    }
    const vars: { [key: string]: string } = {
      '--sav-primary':          color,
      '--sav-primary-soft':     this.hexToRgba(color, 0.14),
      '--sav-primary-xsoft':    this.hexToRgba(color, 0.06),
      '--sav-primary-disabled': this.mixWithWhite(color, 0.48),
    };
    if (this.mode !== 'dark') {
      vars['--sav-page-bg']        = this.mixWithWhite(color, 0.07);
      vars['--sav-border']         = this.mixWithWhite(color, 0.28);
      vars['--sav-locked-bg']      = this.mixWithWhite(color, 0.10);
      vars['--sav-locked-border']  = this.mixWithWhite(color, 0.38);
    }
    return vars;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    Array.from(input.files).forEach(file => {
      const entry: { file: File; preview: string | ArrayBuffer | null } = {file, preview: null};
      const reader = new FileReader();

      reader.onload = () => {
        entry.preview = reader.result;
      };

      reader.readAsDataURL(file);
      this.selectedFiles.push(entry);
    });

    input.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  openFile(fileData: { file: File }): void {
    const url = URL.createObjectURL(fileData.file);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  isImage(fileData: { file: File }): boolean {
    return fileData.file.type.startsWith('image/');
  }

  getFileIcon(fileData: { file: File }): string {
    const mime = fileData.file.type;
    const name = fileData.file.name;

    if (mime === 'application/pdf' || /\.pdf$/i.test(name)) {
      return 'fa-file-pdf';
    }

    if (mime.includes('word') || /\.(doc|docx)$/i.test(name)) {
      return 'fa-file-word';
    }

    if (mime.includes('sheet') || /\.(xls|xlsx|csv)$/i.test(name)) {
      return 'fa-file-excel';
    }

    return 'fa-file';
  }

  isFormValid(): boolean {
    return Boolean(
      this.selectedItem &&
      this.form.customerName.trim() &&
      this.form.title.trim() &&
      this.form.description.trim() &&
      this.form.item &&
      this.form.priority
    );
  }

  private extractCollection(response: any): ApiResource[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.['hydra:member'] || response?.member || [];
  }

  private prepareOptions(options: ApiResource[]): ApiResource[] {
    return options.map(option => ({
      ...option,
      id: this.getResourceValue(option)
    }));
  }

  private applyQueryItem(): void {
    if (!this.requestedItemName) {
      this.itemUnavailable = true;
      this.errorMessage = this.translate.instant('publicSavIssue.invalidLink');
      return;
    }

    const normalizedQueryItem = this.normalizeText(this.requestedItemName);
    const selectedItem = this.items.find(item => this.normalizeText(item.name || '') === normalizedQueryItem);

    if (selectedItem) {
      this.selectedItem = selectedItem;
      this.form.item = selectedItem.id;
      if (selectedItem.color) {
        sessionStorage.setItem(`sav-color:${normalizedQueryItem}`, selectedItem.color);
        this.cachedLoaderColor = selectedItem.color;
      } else {
        sessionStorage.removeItem(`sav-color:${normalizedQueryItem}`);
      }
      return;
    }

    this.itemUnavailable = true;
    this.errorMessage = this.translate.instant('publicSavIssue.productNotFound', { item: this.requestedItemName });
  }

  private buildPayload(): FormData {
    const formData = new FormData();
    formData.append('customerName', this.form.customerName.trim());
    formData.append('customerPhone', this.form.customerPhone.trim());
    formData.append('customerEmail', this.form.customerEmail.trim());
    formData.append('customerAddress', this.form.customerAddress.trim());
    formData.append('title', this.form.title.trim());
    formData.append('description', this.form.description.trim());
    formData.append('item', this.form.item || '');
    formData.append('priority', this.form.priority || '');

    if (this.form.type) {
      formData.append('type', this.form.type);
    }

    this.selectedFiles.forEach(entry => {
      formData.append('files[]', entry.file, entry.file.name);
    });

    return formData;
  }

  private getResourceValue(resource: ApiResource): string {
    if (resource['@id']) {
      return resource['@id'];
    }

    return resource.id ? `/api/public/sav/items/${resource.id}` : '';
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private mixWithWhite(hex: string, factor: number): string {
    const r = Math.round(255 + (parseInt(hex.slice(1, 3), 16) - 255) * factor);
    const g = Math.round(255 + (parseInt(hex.slice(3, 5), 16) - 255) * factor);
    const b = Math.round(255 + (parseInt(hex.slice(5, 7), 16) - 255) * factor);
    return `rgb(${r}, ${g}, ${b})`;
  }

  private normalizeText(value: string): string {
    return value.trim().toLowerCase();
  }

  private initializeLanguage(): void {
    const current = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.currentLanguage = current === 'fr' ? 'fr' : 'en';
    this.translate.use(this.currentLanguage);
  }

  private initializeMode(): void {
    this.mode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private resetFormAfterSuccess(): void {
    const selectedItem = this.form.item;

    this.form = {
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      customerAddress: '',
      title: '',
      description: '',
      item: selectedItem,
      type: null,
      priority: null
    };
    this.selectedFiles = [];
  }
}
